import React from 'react'
import './../styles/HeroAnimation.css'

/**
 * HeroAnimation
 * Lightweight, dependency-free animated SVG that replaces the static hero image.
 * Theme: software quality / app development — orbiting code-window, floating
 * test-tick badges, animated gradient blob, and pulsing connection lines.
 */
const HeroAnimation = () => (
  <div className="hero-anim" aria-hidden="true">
    <svg
      className="hero-anim__svg"
      viewBox="0 0 520 520"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Animated illustration of software quality"
    >
      <defs>
        <linearGradient id="qa-grad-primary" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#0066ff" />
          <stop offset="100%" stopColor="#00d4ff" />
        </linearGradient>
        <linearGradient id="qa-grad-glass" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="rgba(255,255,255,0.10)" />
          <stop offset="100%" stopColor="rgba(255,255,255,0.02)" />
        </linearGradient>
        <radialGradient id="qa-grad-blob" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#00d4ff" stopOpacity="0.55" />
          <stop offset="60%" stopColor="#0066ff" stopOpacity="0.20" />
          <stop offset="100%" stopColor="#0066ff" stopOpacity="0" />
        </radialGradient>
        <filter id="qa-glow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="6" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* Background glow blob */}
      <circle cx="260" cy="260" r="220" fill="url(#qa-grad-blob)" className="hero-anim__blob" />

      {/* Orbiting dotted ring */}
      <g className="hero-anim__orbit" transform="translate(260 260)">
        <circle r="200" fill="none" stroke="rgba(0, 212, 255, 0.25)" strokeWidth="1" strokeDasharray="3 8" />
        <circle r="160" fill="none" stroke="rgba(0, 102, 255, 0.18)" strokeWidth="1" strokeDasharray="2 6" />
      </g>

      {/* Counter-rotating ring */}
      <g className="hero-anim__orbit-reverse" transform="translate(260 260)">
        <circle r="240" fill="none" stroke="rgba(0, 212, 255, 0.12)" strokeWidth="1" strokeDasharray="1 10" />
      </g>

      {/* Central code window */}
      <g className="hero-anim__window">
        <rect
          x="130"
          y="160"
          width="260"
          height="200"
          rx="16"
          ry="16"
          fill="url(#qa-grad-glass)"
          stroke="rgba(255,255,255,0.18)"
          strokeWidth="1"
        />
        {/* window dots */}
        <circle cx="150" cy="180" r="4" fill="#ff6b6b" />
        <circle cx="164" cy="180" r="4" fill="#ffbb33" />
        <circle cx="178" cy="180" r="4" fill="#00c851" />
        {/* code lines */}
        <rect x="150" y="206" width="120" height="6" rx="3" fill="rgba(0,212,255,0.55)" className="hero-anim__line hero-anim__line--1" />
        <rect x="150" y="222" width="180" height="6" rx="3" fill="rgba(255,255,255,0.30)" className="hero-anim__line hero-anim__line--2" />
        <rect x="170" y="238" width="140" height="6" rx="3" fill="rgba(0,102,255,0.55)" className="hero-anim__line hero-anim__line--3" />
        <rect x="150" y="254" width="200" height="6" rx="3" fill="rgba(255,255,255,0.20)" className="hero-anim__line hero-anim__line--4" />
        <rect x="170" y="270" width="100" height="6" rx="3" fill="rgba(0,212,255,0.50)" className="hero-anim__line hero-anim__line--5" />
        <rect x="150" y="286" width="160" height="6" rx="3" fill="rgba(255,255,255,0.25)" className="hero-anim__line hero-anim__line--6" />
        {/* progress / passing tests bar */}
        <rect x="150" y="312" width="220" height="8" rx="4" fill="rgba(255,255,255,0.08)" />
        <rect x="150" y="312" width="220" height="8" rx="4" fill="url(#qa-grad-primary)" className="hero-anim__progress" />
        <rect x="150" y="332" width="80" height="6" rx="3" fill="rgba(0,200,81,0.7)" />
      </g>

      {/* Floating tick badge (top-right) */}
      <g className="hero-anim__badge hero-anim__badge--tick" filter="url(#qa-glow)">
        <circle cx="410" cy="140" r="28" fill="url(#qa-grad-primary)" />
        <path
          d="M398 141 L407 150 L423 132"
          fill="none"
          stroke="#fff"
          strokeWidth="4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>

      {/* Floating gear badge (bottom-left) */}
      <g className="hero-anim__badge hero-anim__badge--gear" transform="translate(110 380)" filter="url(#qa-glow)">
        <circle r="26" fill="rgba(255,255,255,0.06)" stroke="rgba(0,212,255,0.45)" strokeWidth="1.5" />
        <g className="hero-anim__gear">
          <circle r="8" fill="none" stroke="#00d4ff" strokeWidth="2" />
          <g stroke="#00d4ff" strokeWidth="2" strokeLinecap="round">
            <line x1="0" y1="-14" x2="0" y2="-18" />
            <line x1="0" y1="14" x2="0" y2="18" />
            <line x1="-14" y1="0" x2="-18" y2="0" />
            <line x1="14" y1="0" x2="18" y2="0" />
            <line x1="-10" y1="-10" x2="-13" y2="-13" />
            <line x1="10" y1="10" x2="13" y2="13" />
            <line x1="-10" y1="10" x2="-13" y2="13" />
            <line x1="10" y1="-10" x2="13" y2="-13" />
          </g>
        </g>
      </g>

      {/* Floating chart badge (bottom-right) */}
      <g className="hero-anim__badge hero-anim__badge--chart" transform="translate(420 380)" filter="url(#qa-glow)">
        <circle r="26" fill="rgba(255,255,255,0.06)" stroke="rgba(0,102,255,0.45)" strokeWidth="1.5" />
        <g stroke="#0066ff" strokeWidth="3" strokeLinecap="round">
          <line x1="-12" y1="8" x2="-12" y2="-2" />
          <line x1="-2" y1="8" x2="-2" y2="-8" />
          <line x1="8" y1="8" x2="8" y2="-12" />
        </g>
      </g>

      {/* Connection dots / particles */}
      <g className="hero-anim__particles">
        <circle cx="80" cy="200" r="3" fill="#00d4ff" className="hero-anim__particle hero-anim__particle--1" />
        <circle cx="450" cy="240" r="2.5" fill="#0066ff" className="hero-anim__particle hero-anim__particle--2" />
        <circle cx="200" cy="100" r="2" fill="#00d4ff" className="hero-anim__particle hero-anim__particle--3" />
        <circle cx="350" cy="450" r="3" fill="#0066ff" className="hero-anim__particle hero-anim__particle--4" />
        <circle cx="60" cy="320" r="2" fill="#00d4ff" className="hero-anim__particle hero-anim__particle--5" />
        <circle cx="470" cy="80" r="2.5" fill="#00d4ff" className="hero-anim__particle hero-anim__particle--6" />
      </g>
    </svg>
  </div>
)

export default HeroAnimation
